import React, { Component, ErrorInfo, ReactNode } from 'react';
import { reportError } from '../utils/telemetry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  pageName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, componentStack: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { pageName } = this.props;
    console.error(
      `ErrorBoundary caught${pageName ? ` on [${pageName}]` : ''}:`,
      error,
      errorInfo.componentStack,
    );
    this.setState({ componentStack: errorInfo.componentStack ?? null });
    reportError(error, { pageName });
  }

  private handleResetAndReload = () => {
    // 상태 초기화 후 페이지 새로고침
    this.setState({ hasError: false, error: null, componentStack: null });
    window.location.reload();
  };

  private handleGoHome = () => {
    // 홈으로 이동
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      const { pageName } = this.props;
      const isDarkMode = document.documentElement.classList.contains('dark');

      return (
        <div
          role="alert"
          className={`flex flex-col items-center justify-center min-h-[calc(100vh-72px)] px-4 py-8 text-center ${
            isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
          }`}
        >
          {/* 일러스트 */}
          <div className="mb-6">
            <svg
              className="w-24 h-24 mx-auto"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {/* 미니디스크 아이콘 */}
              <circle cx="12" cy="12" r="10" className={isDarkMode ? 'text-gray-600' : 'text-gray-300'} />
              <circle cx="12" cy="12" r="6" className={isDarkMode ? 'text-gray-700' : 'text-gray-200'} />
              <circle cx="12" cy="12" r="3" className={isDarkMode ? 'text-red-500' : 'text-red-400'} />
              <path d="M 8 12 L 4 12 M 20 12 L 16 12 M 12 8 L 12 4 M 12 20 L 12 16" className={isDarkMode ? 'text-gray-500' : 'text-gray-400'} />
            </svg>
          </div>

          {/* 제목 */}
          <h2 className={`text-2xl font-bold mb-3 ${
            isDarkMode ? 'text-gray-100' : 'text-gray-900'
          }`}>
            앗, 문제가 발생했어요!
          </h2>

          {/* 설명 메시지 */}
          <p className={`text-sm mb-2 max-w-md ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {pageName
              ? `${pageName} 페이지를 불러오는 중 예상치 못한 오류가 발생했습니다.`
              : '애플리케이션을 불러오는 중 예상치 못한 오류가 발생했습니다.'}
          </p>

          {/* 도움말 */}
          <p className={`text-xs mb-8 max-w-md ${
            isDarkMode ? 'text-gray-500' : 'text-gray-500'
          }`}>
            아래 버튼으로 다시 시도하거나 홈으로 돌아갈 수 있습니다.
            {pageName && ' 다른 페이지는 정상 작동합니다.'}
          </p>

          {/* 액션 버튼 */}
          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            <button
              onClick={this.handleResetAndReload}
              aria-label="페이지 새로고침"
              className={`px-6 py-3 font-medium rounded-lg transition-colors whitespace-nowrap ${
                isDarkMode
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              다시 시도
            </button>
            <button
              onClick={this.handleGoHome}
              aria-label="홈으로 이동"
              className={`px-6 py-3 font-medium rounded-lg transition-colors whitespace-nowrap ${
                isDarkMode
                  ? 'border border-gray-600 text-gray-300 hover:bg-gray-800'
                  : 'border border-gray-300 text-gray-700 hover:bg-gray-100'
              }`}
            >
              홈으로 이동
            </button>
          </div>

          {/* 개발 환경: 상세 에러 정보 */}
          {import.meta.env.DEV && this.state.error && (
            <div className={`w-full max-w-2xl rounded-lg p-4 ${
              isDarkMode ? 'bg-red-900/30 border border-red-700' : 'bg-red-50 border border-red-200'
            }`}>
              <h3 className={`text-xs font-semibold mb-2 ${
                isDarkMode ? 'text-red-400' : 'text-red-800'
              }`}>
                개발자 정보 (개발 환경에서만 표시)
              </h3>
              <pre className={`text-xs overflow-auto max-h-48 rounded ${
                isDarkMode ? 'bg-gray-950 text-red-300' : 'bg-white text-red-700'
              } p-3 whitespace-pre-wrap break-words`}>
                {`에러: ${this.state.error.message}\n`}
                {this.state.componentStack && `\n컴포넌트 스택:\n${this.state.componentStack}`}
              </pre>
            </div>
          )}

          {/* 피드백 텍스트 */}
          <p className={`text-xs mt-8 ${
            isDarkMode ? 'text-gray-600' : 'text-gray-500'
          }`}>
            문제가 계속되면 앱을 다시 시작하거나 지원팀에 문의하세요.
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
